import { gql, useLazyQuery } from '@apollo/client';
import { openPDF } from '@/utils/navigation';
import {
  OrderSummary_GetOrderPdfQuery,
  OrderSummary_GetOrderPdfQueryVariables,
} from '@/types';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useState } from 'react';
import { useToasts } from '@/contexts/toast-context';

export const GET_ORDER_PDF = gql`
  query OrderSummary_GetOrderPdf(
    $orderUUID: uuid!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $showItemDescriptions: Boolean
  ) {
    getOrderPdf(
      order_id: $orderUUID
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      show_item_descriptions: $showItemDescriptions
    ) {
      url
    }
  }
`;

export const useOpenOrderPDF = () => {
  const { showErrorToast } = useToasts();
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID: restaurantUUID,
  } = useAuthenticatedUserContext();
  const [loadingPDF, setLoadingPDF] = useState(false);

  const [getOrderPDF] = useLazyQuery<
    OrderSummary_GetOrderPdfQuery,
    OrderSummary_GetOrderPdfQueryVariables
  >(GET_ORDER_PDF);

  const openOrderPDF = async ({
    orderUUID,
    showItemDescriptions,
  }: {
    orderUUID: string;
    showItemDescriptions?: boolean;
  }) => {
    try {
      setLoadingPDF(true);

      await openPDF(async () => {
        const { data } = await getOrderPDF({
          fetchPolicy: 'network-only',
          variables: {
            orderUUID,
            restaurantUUID,
            showItemDescriptions,
            supplierUUID,
          },
        });

        const pdfURL = data?.getOrderPdf.url;
        if (!pdfURL) {
          throw new Error('Unable to retrieve order PDF');
        }

        return pdfURL;
      });
    } catch (e) {
      showErrorToast();
    } finally {
      setLoadingPDF(false);
    }
  };

  return {
    loadingPDF,
    openOrderPDF,
  };
};
